class RiskCatalog {
    constructor() {
        this.Id = 0;
        this.Version = "";
        this.RiskFamilies = [];
        this.IsActive = false;
    }

    FindRiskFamily(reference) {
        for (let rColl of this.RiskFamilies) {
            if (rColl.Major === reference)
                return rColl;
        }
        return null;
    }

    ToString() {
        return `Catalog v${this.Version}`;
    }
}

class RiskFamily {
    constructor() {
        this.Major = 0;
        this.Description = "";
        this.ShortDescription = "";
        this.Risks = [];
        this.HelpWiki = [];
    }

    ToString() {
        return `${this.Major}. ${this.ShortDescription}`;
    }
}

class Risk {
    constructor() {
        this.Major = 0;
        this.Minor = 0;
        this.Sub = 0;
        this._ref = null;
        this._description = "";
        this.ShortDescription = "";
        this.SubItems = [];
        this.ParentRisk = null;
        this.DefaultConsequences = "";
        this.DefaultConsequencesConcern = "";
        this.DefaultSolutions = [];
    }

    get Ref() {
        if (this._ref !== null) return this._ref;
        if (this.Major === 0 && this.Minor === 0) {
            this._ref = "";
        } else {
            if (this.Sub > 0)
                this._ref = `${this.Major}.${this.Minor}.${this.Sub}`;
            else
                this._ref = `${this.Major}.${this.Minor}`;
        }
        return this._ref;
    }

    set Ref(value) {
        this._ref = value;
    }

    get HasSubItems() {
        return this.SubItems && this.SubItems.length > 0;
    }

    get Description() {
        return this._description
    }

    set Description(value) {
            if (value.includes("\n*")) {
                this.SubItems = [];
                const lines = value.split('\n');
                this._description = lines[0].replace(/\r/g, "");
                for (let i = 1; i < lines.length; i++) {
                    const sr = new Risk();
                    sr.Description = lines[i].replace(/\r/g, "");
                    sr.ShortDescription = sr.Description;
                    sr.Major = this.Major;
                    sr.Minor = this.Minor;
                    sr.Sub = i;
                    this.SubItems.push(sr);
                }
            } else {
                this._description = value;
            }
    }

    GetDescription() {
        if (!this.HasSubItems) {
            return this.Description;
        } else {
            let description = this.description;
            for (let i = 0; i < this.subItems.length; i++) {
                description += `\n${this.subItems[i].description}`;
            }
            return description;
        }
    }

    ToString() {
        return `${this.Ref}. ${this.ShortDescription}`;
    }
}

const EnumRiskConcern = {
    NotSet: 0,
    Yes: 1,
    No: 2,
    NotApplicable: 3
};

class RiskReportItem {
    constructor() {
        this.Document = null;
        this.Catalog = null;
        this.RiskRef = "";
        this.ManualDescription = "";
        this.Concern = EnumRiskConcern.NotSet;
        this.Consequences = "";
        this.CurrentPrevention = "";
        this.ActionsCompletedOn = "";
        this.SubItems = [];
        this.Hidden = false;
    }

    getDocument() {
        return this.Document;
    }

    setCatalog(doc) {
        this.Document = doc;
        this.Catalog = doc.RiskCatalog;
        if (this.HasSubItems) {
            for (let ri of this.SubItems)
                ri.setCatalog(doc);
        }
    }

    get Risk() {
        if (this.Catalog !== null) {
            const r = this.Document.FindRisk(this.RiskRef);
            if (r === null) {
                throw new Error(`Le risque '${this.RiskRef}' n'a pas été trouvé dans le catalogue. Appeler la méthode Document.UpdateRiskReportsFamilies() pour corriger ce problème.`);
            }
            return r;
        } else {
            throw new Error("Utiliser RiskReportItem.SetCatalog() avant d'utiliser cette propriété.");
        }
    }

    get ConcernEnum() {
        return Object.keys(EnumRiskConcern).find(key => EnumRiskConcern[key] === this.Concern);
    }

    set ConcernEnum(value) {
        this.Concern = EnumRiskConcern[value];
    }

    get HasSubItems() {
        return this.SubItems && this.SubItems.length > 0;
    }

    allSubItemsHidden() {
        if (!this.SubItems || this.SubItems.length === 0) return false;

        for (const si of this.SubItems) {
            if (!si.Hidden) return false;
        }

        return true;
    }

    ToString() {
        return `${this.Risk.ToString()} => ${this.ConcernEnum}`;
    }
}

const EnumStatus = {
    New: 0,
    Incomplete: 1,
    Complete: 2
};


class RiskReport {
    constructor() {
        this.FamilyRef = 0;
        this.Risks = [];
        this.IdentifiedRisks = [];
        this.WorkingUnits = [];
        this._catalog = null;
        this._document = null;
        this.IsOneOrMoreRiskIdentified = false;
    }

    get Family() {
        if (this._catalog !== null) {
            const f = this._catalog.FindRiskFamily(this.FamilyRef);
            if (f === null)
                throw new Error(`La famille '${this.FamilyRef}' n'a pas été trouvée dans le catalogue. Appeler la méthode Document.UpdateRiskReportsFamilies() pour corriger ce problème.`);
            return f;
        } else {
            throw new Error("Utiliser SetFamilyRef() avant d'utiliser cet attribut.");
        }
    }

    get Catalog() {
        return this._catalog;
    }

    setCatalog(document) {
        this._document = document;
        this._catalog = document.RiskCatalog;
    }

    getDocument() {
        return this._document;
    }

    get CountRisksAndSubRisks() {
        let count = 0;
        for (const r of this.Risks) {
            count++;
            if (r.HasSubItems)
                count += r.SubItems.length;
        }
        return count;
    }

    fillFromRiskFamily(riskFamilies) {
        this.FamilyRef = riskFamilies.Major;
        for (const re of riskFamilies.Risks) {
            this.addFromRisk(re);
        }
    }

    addFromRisk(re) {
        const rr = new RiskReportItem();
        rr.setCatalog(this._document);
        rr.RiskRef = re.Ref;
        this.Risks.push(rr);
        this.addSubItems(re, rr);
        return rr;
    }

    addSubItems(re, rr) {
        if (re.HasSubItems) {
            rr.SubItems = [];
            for (const sr of re.SubItems) {
                const rrsi = new RiskReportItem();
                rrsi.setCatalog(this._document);
                rrsi.RiskRef = sr.Ref;
                rr.SubItems.push(rrsi);
            }
        }
    }

    fillWorkingUnitsChecked(workingUnits) {
        const oldList = [...this.WorkingUnits];
        this.WorkingUnits = [];
        for (const w of workingUnits) {
            const wuc = new WorkingUnitChecked();
            wuc.WorkingUnit = w;
            const oldwuc = oldList.find(o => o.WorkingUnit !== null && o.WorkingUnit.Name.trim() === w.Name.trim());
            if (oldwuc !== undefined) {
                wuc.Checked = oldwuc.Checked;
            }
            this.WorkingUnits.push(wuc);
        }
    }

    newRiskReportAuditItemFromRiskReportItem(risk) {
        const ra = new RiskReportAuditItem();
        ra.Risk = this.getDocument().FindRisk(risk.Ref);
        return ra;
    }

    get Status() {
        let countOk = 0;
        let countSub = 0;
        for (const ri of this.Risks) {
            if (!ri.HasSubItems) {
                if (ri.Concern !== EnumRiskConcern.NotSet || (ri.ManualDescription === "" && ri.Risk.Description === "" && ri.Concern === EnumRiskConcern.NotSet)) {
                    countOk++;
                }
            } else {
                let countOKSubs = 0;
                for (const ris of ri.SubItems) {
                    if (ris.Concern !== EnumRiskConcern.NotSet) {
                        countOKSubs++;
                    }
                }
                if (countOKSubs === ri.SubItems.length) {
                    countOk++;
                } else {
                    countSub += countOKSubs;
                }
            }
        }
        if (countOk === this.Risks.length) {
            return EnumStatus.Complete;
        }
        if (countOk > 0) {
            return EnumStatus.Incomplete;
        }
        if (countSub > 0) {
            return EnumStatus.Incomplete;
        }
        return EnumStatus.New;
    }

    ToString() {
        return `${this.Family.ToString()} - Risks Identified: ${this.IdentifiedRisks.length}`;
    }
}

const EnumExposureFrequency = {
    None: 0,
    Rare: 1,
    Casual: 2,
    Timely: 3,
    Frequent: 4,
    Permanent: 5
};

const EnumProtectionLevel = {
    None: 0,
    VeryEffective: 1,
    Effective: 2,
    Medium: 3,
    Ineffective: 4,
    Nonexistant: 5
};

const EnumSeverityLevel = {
    None: 0,
    VeryLow: 1,
    Low: 2,
    Medium: 3,
    High: 4,
    VeryHigh: 5
};

class RiskReportAuditItem {
    constructor() {
        this.ExposureFrequency = EnumExposureFrequency.Rare;
        this.ProtectionLevel = EnumProtectionLevel.VeryEffective;
        this.SeverityLevel = EnumSeverityLevel.VeryLow;
        this._familyReport = null;
        this.ActionPlanDescription = "";
        this.ActionPlanDueDate = "";
        this.ActionPlanCompletionDate = "";
        this.ActionPlanResponsiblePerson = "";
        this.ActionPlanCompletionAction = "";
    }

    setRiskReport(familyReport) {
        this._familyReport = familyReport;
    }

    getRiskReport() {
        return this._familyReport;
    }

    get RiskValuation() {
        return this.ExposureFrequency * this.SeverityLevel * this.ProtectionLevel;
    }

    get Priority() {
        if (!this._priority)
            this._priority = PdfDocumentUnique.Priorisations[this.SeverityLevel - 1][this.ExposureFrequency - 1];
        return this._priority;
    }

    set Priority(value) {
        this._priority = value;
    }

    ToString() {
        return `${this.Risk.ToString()} : ${ToString(this.ExposureFrequency)} x ${ToString(this.SeverityLevel)} x ${ToString(this.ProtectionLevel)} = ${this.RiskValuation}`;
    }
}

module.exports = {
    Risk,
    RiskCatalog,
    RiskFamily,
    RiskReport,
    RiskReportAuditItem,
    RiskReportItem,
    EnumExposureFrequency,
    EnumProtectionLevel,
    EnumRiskConcern,
    EnumSeverityLevel,
    EnumStatus
} 

